import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { ToastrService } from 'ngx-toastr';

import { Facility } from '../firestore-models/facility';
import { FacilityAttribute } from '../firestore-models/facility-attribute';
import { ProjectLanguage } from '../firestore-models/project-language';
import { ApiFacility, ApiSetFacilitiesRequest, ApiSplatsKeyDevice, ApiSplatsUnit } from '../api-client-module';

import { ApiClientService } from '../api-client.service';
import { ConfigurationService } from '../configuration.service';
import { SpinnerService } from '../services/spinner.service';
import { AppConst } from '../app-const';

import { FacilityDialogComponent } from './facility-dialog/facility-dialog.component';
import { MessageDialogComponent } from '../common/message-dialog/message-dialog.component';
import { ReflectDataDialogComponent } from './reflect-data-dialog/reflect-data-dialog.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-facility',
  templateUrl: './facility.component.html',
  styleUrls: ['./facility.component.css'],
  standalone: false,
})
export class FacilityComponent implements OnInit, OnDestroy {

  adminUser: boolean = false;

  //Facilityコレクション
  private facilitiesCollection!: AngularFirestoreCollection<Facility>;
  facilitiesObservable!: Observable<Facility[]>;
  facilitiesSubscribe!: Subscription;
  private facilityAttributesCollection!: AngularFirestoreCollection<FacilityAttribute>;
  facilityAttributesObservable!: Observable<FacilityAttribute[]>;
  facilityAttributesSubscribe!: Subscription;
  //各施設情報に紐付くプロジェクトID
  projectId: string;
  //各施設情報に紐付くプロジェクトの言語情報
  projectLanguages!: Array<ProjectLanguage>;
  //施設登録リミット
  facilityRegiLimit: number = 1;

  //施設情報テーブル
  displayedColumns: string[] = ['position', 'edit', 'select', 'facility_id', 'attribute', 'name', 'floor', 'file', 'date', 'reflect'];
  dataSource = new MatTableDataSource<Facility>();
  selection = new SelectionModel<Facility>(true, []);

  attrCollectionName = AppConst.FIRESTORE_FACILITY_ATTR;
  uploadedFilesCollectionName = AppConst.FIRESTORE_UPLOADED_FILES;

  protected ATTR_SEARCH_WORD_PREFIX: string = 'facilityAttributes';
  facilityAttributes!: Array<FacilityAttribute>;

  searchFilter: any = {};

  projectBaseLang: string = 'ja';

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor(
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private configurationService: ConfigurationService,
    private translate: TranslateService
  ) {
    this.projectId = this.configurationService.getProjectId();
    if (this.configurationService.getUserType() == AppConst.MANAGE_USER_TYPE_ADMIN) {
      this.adminUser = true;
    }
    const limit = this.configurationService.getProject().facility_registration_limit;
    if (limit) {
      this.facilityRegiLimit = limit;
    }
  }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.setCustomFilter();
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
    // 施設取得
    this.facilitiesCollection = this.angularFirestore.collection<Facility>(
      AppConst.FIRESTORE_FACILITIES, ref => ref.where('project_id', '==', this.projectId).orderBy('facility_id', 'asc'));
    this.facilitiesObservable = this.facilitiesCollection.valueChanges({ idField: 'id' });
    this.facilitiesSubscribe = this.facilitiesObservable.subscribe(facilities => {
      this.dataSource.data = this.addFilterValue(facilities);
    });
    // 施設属性取得
    this.facilityAttributesCollection = this.angularFirestore.collection<FacilityAttribute>(
      AppConst.FIRESTORE_FACILITY_ATTR, ref => ref.where('project_id', '==', this.projectId)
        .orderBy('attribute_id', 'asc'));
    this.facilityAttributesObservable = this.facilityAttributesCollection.valueChanges({ idField: 'id' });
    this.facilityAttributesSubscribe = this.facilityAttributesObservable.subscribe(facilityAttributes => {
      this.facilityAttributes = facilityAttributes;
    });
  }

  ngOnDestroy(): void {
    this.facilitiesSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: Facility): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  //編集ダイアログ
  openEditDialog(facility: Facility): void {
    let apiFacility: ApiFacility = {
      facility_id: facility.facility_id,
      facility_attribute_id: facility.facility_attribute_id,
      facility_names: facility.facility_names,
      facility_descs: facility.facility_descs,
      floor_ids: facility.floor_ids,
      favicon_file: facility.favicon_file,
      logo_files: facility.logo_files,
      facility_files: facility.facility_files,
      facility_file_link: facility.facility_file_link,
      youtube_codes: facility.youtube_codes,
      use_youtube: facility.use_youtube,
      externallink_names: facility.externallink_names,
      externallink_urls: facility.externallink_urls,
      use_externallink: facility.use_externallink,
      purchase_titles: facility.purchase_titles,
      latitude: facility.facility_geopoint.latitude,
      longitude: facility.facility_geopoint.longitude,
      map_zoom_top: facility.map_zoom_top ? facility.map_zoom_top : 16,
      map_zoom_map: facility.map_zoom_map ? facility.map_zoom_map : 18,
      addresses: facility.addresses,
      inquiries: facility.inquiries,
      policies: facility.policies,
      copyrights: facility.copyrights,
      faqs: facility.faqs,
      theme_color: facility.theme_color,
      meta_keywords: facility.meta_keywords,
      meta_description: facility.meta_description,
      google_analytics_code: facility.google_analytics_code,
      site_url: facility.site_url,
      site_url_staging: facility.site_url_staging,
      telephone_number: facility.telephone_number,
      map_disp_type: facility.map_disp_type,
      visible_good: facility.visible_good,
      unvisible_breadcrumb: facility.unvisible_breadcrumb,
      unvisible_guide_number: facility.unvisible_guide_number,
      unvisible_floor_name: facility.unvisible_floor_name,
      unvisible_reservation_facility_name: facility.unvisible_reservation_facility_name,
      use_guide: facility.use_guide,
      unvisible_enter_num: facility.unvisible_enter_num,
      use_number_entry: facility.use_number_entry,
      use_enter_num_title: facility.use_enter_num_title,
      enter_num_titles: facility.enter_num_titles,
      enter_num_helps: facility.enter_num_helps,
      visible_enquete: facility.visible_enquete,
      use_question: facility.use_question,
      visible_enquete_thanks: facility.visible_enquete_thanks,
      use_enquete_thanks_title: facility.use_enquete_thanks_title,
      enquete_thanks_titles: facility.enquete_thanks_titles,
      visible_ranking: facility.visible_ranking,
      use_ranking: facility.use_ranking,
      visible_shelter: facility.visible_shelter,
      use_shelter: facility.use_shelter,
      visible_paid_content: facility.visible_paid_content,
      use_paid_content: facility.use_paid_content,
      visible_all_map: facility.visible_all_map,
      use_all_map: facility.use_all_map,
      use_entry_form: facility.use_entry_form,
      not_overlap_guide: facility.not_overlap_guide,
      top_text_align_left: facility.top_text_align_left,
      floor_text_align_left: facility.floor_text_align_left,
      exhibit_text_align_left: facility.exhibit_text_align_left,
      menu_style: facility.menu_style,
      language_icon_type: facility.language_icon_type || 'globe',
      voice_control_type: facility.voice_control_type,
      pin_tap_voice_control_type: facility.pin_tap_voice_control_type,
      splats_key_devices: facility.splats_key_devices,
      project_id: facility.project_id,
      enabled: facility.enabled,
    };
    const dialogRef = this.dialog.open(FacilityDialogComponent, {
      width: '85%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_UPDATE, facility: apiFacility }
    });
  }

  //追加ダイアログ
  openAddDialog(): void {
    const dialogRef = this.dialog.open(FacilityDialogComponent, {
      width: '85%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_ADD, facility: null }
    });
  }

  //削除
  deleteData(): void {
    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '420px',
      height: '180px',
      disableClose: true,
      data: { message: this.translate.instant('選択されたデータを削除します。よろしいですか？') }
    });

    dialogRef.afterClosed().subscribe(result => {
      let selectedFacilities: Array<ApiFacility> = new Array<ApiFacility>();
      for (const row of this.dataSource.data) {
        if (this.selection.isSelected(row)) {
          const selectedFacility: ApiFacility = {
            facility_id: row.facility_id,
            facility_attribute_id: row.facility_attribute_id,
            facility_names: row.facility_names,
            facility_descs: row.facility_descs,
            floor_ids: row.floor_ids,
            favicon_file: row.favicon_file,
            logo_files: row.logo_files,
            facility_files: row.facility_files,
            facility_file_link: row.facility_file_link,
            youtube_codes: row.youtube_codes,
            use_youtube: row.use_youtube,
            externallink_names: row.externallink_names,
            externallink_urls: row.externallink_urls,
            use_externallink: row.use_externallink,
            purchase_titles: row.purchase_titles,
            latitude: row.facility_geopoint.latitude,
            longitude: row.facility_geopoint.longitude,
            map_zoom_top: row.map_zoom_top,
            map_zoom_map: row.map_zoom_map,
            addresses: row.addresses,
            inquiries: row.inquiries,
            policies: row.policies,
            copyrights: row.copyrights,
            faqs: row.faqs,
            theme_color: row.theme_color,
            meta_keywords: row.meta_keywords,
            meta_description: row.meta_description,
            google_analytics_code: row.google_analytics_code,
            site_url: row.site_url,
            site_url_staging: row.site_url_staging,
            telephone_number: row.telephone_number,
            map_disp_type: row.map_disp_type,
            visible_good: row.visible_good,
            unvisible_breadcrumb: row.unvisible_breadcrumb,
            unvisible_guide_number: row.unvisible_guide_number,
            unvisible_floor_name: row.unvisible_floor_name,
            unvisible_reservation_facility_name: row.unvisible_reservation_facility_name,
            use_guide: row.use_guide,
            unvisible_enter_num: row.unvisible_enter_num,
            use_number_entry: row.use_number_entry,
            use_enter_num_title: row.use_enter_num_title,
            enter_num_titles: row.enter_num_titles,
            enter_num_helps: row.enter_num_helps,
            visible_enquete: row.visible_enquete,
            use_question: row.use_question,
            visible_enquete_thanks: row.visible_enquete_thanks,
            use_enquete_thanks_title: row.use_enquete_thanks_title,
            enquete_thanks_titles: row.enquete_thanks_titles,
            visible_ranking: row.visible_ranking,
            use_ranking: row.use_ranking,
            visible_shelter: row.visible_shelter,
            use_shelter: row.use_shelter,
            visible_paid_content: row.visible_paid_content,
            use_paid_content: row.use_paid_content,
            visible_all_map: row.visible_all_map,
            use_all_map: row.use_all_map,
            use_entry_form: row.use_entry_form,
            not_overlap_guide: row.not_overlap_guide,
            top_text_align_left: row.top_text_align_left,
            floor_text_align_left: row.floor_text_align_left,
            exhibit_text_align_left: row.exhibit_text_align_left,
            menu_style: row.menu_style,
            language_icon_type: row.language_icon_type || 'globe',
            voice_control_type: row.voice_control_type,
            pin_tap_voice_control_type: row.pin_tap_voice_control_type,
            splats_key_devices: row.splats_key_devices,
            project_id: row.project_id,
            enabled: row.enabled,
          };
          selectedFacilities.push(selectedFacility);
        }
      }
      if (!!result) {
        let request: ApiSetFacilitiesRequest = {
          access_token: '',
          change_type: AppConst.DATA_CHANGE_TYPE_DELETE,
          facilities: selectedFacilities
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setFacilities(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('削除しました。'));
            } else {
              this.toastr.error(this.translate.instant('削除できませんでした。'));
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('削除できませんでした。'));
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
            this.selection.clear();
          }
        );
      }
    });
  }

  // データ本番反映
  reflectData(facility: Facility): void {
    const dialogRef = this.dialog.open(ReflectDataDialogComponent, {
      width: '50%',
      disableClose: true,
      data: {
        projectId: facility.project_id,
        facilityId: facility.facility_id
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        console.log(result);
      }
    });
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'facility_id':
          return compare(a.facility_id, b.facility_id, isAsc);
        case 'name':
          const aName = a.facility_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code })[0].name;
          const bName = b.facility_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code })[0].name;
          return compare(aName, bName, isAsc);
        case 'date':
          return compare(a.updated.toMillis(), b.updated.toMillis(), isAsc);
        default:
          return 0;
      }
    });
  }

  setCustomFilter() {
    // NOTE: AND検索
    this.dataSource.filterPredicate = (data: any, filter: any) => {
      let matchCount = 0;
      let keys = Object.keys(filter);
      for (const key of keys) {
        if (!filter[key]) {
          // フィルタに値がない場合は全抽出
          matchCount++;
        } else {
          if (data[key].trim().toLowerCase().indexOf(filter[key]) !== -1) {
            matchCount++;
          }
        }
      }
      return (keys.length === matchCount);
    };
  }

  addFilterValue(docs: Array<any>): Array<any> {
    for (const doc of docs) {
      let filterValue = '';
      for (const value of doc.facility_names) {
        filterValue += value.name + ' '
      }
      doc['filterValue'] = filterValue;
      doc['filterValueAttr'] = this.ATTR_SEARCH_WORD_PREFIX + doc.facility_attribute_id;
    }
    return docs;
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchFilter['filterValue'] = filterValue.trim().toLowerCase();
    this.dataSource.filter = this.searchFilter;
  }

  applyFilterAttr(event: any) {
    this.selection.clear();
    let filterValue = '';
    if (event.value) {
      filterValue = this.ATTR_SEARCH_WORD_PREFIX + event.value;
    }
    this.searchFilter['filterValueAttr'] = filterValue.trim().toLowerCase();
    this.dataSource.filter = this.searchFilter;
  }

}
